import {
  type EdgeData,
  type EdgeId,
  GraphConstants,
  type RGBColorNumber,
  type RenderableEdgeData,
  getEdgeStyleLineColor,
  isRenderableEdgeData,
} from '@ipfabric/utils';
import {
  type GraphEdgeDisplayObject,
  drawEdgeArrow,
  drawEdgeCircle,
  drawEdgeLine,
} from 'components/Graph/GraphApp/GraphEdge';
import type {
  GraphRendererPlugin,
  GraphRendererPluginInterface,
} from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import type { UnhighlightEdgeEvent } from 'components/Graph/model/GraphCommandEvents';
import { Graphics } from 'pixi.js';
import { omit } from 'utils/object/omit';
import type { RunningAnimation } from 'utils/pixi/animation/AnimationController';
import type { EdgeBoundsChangeEvent } from '../events/GraphAppEvents';
import { ZINDEX_EDGE_HIGHLIGHT_LINE } from '../zIndexes';

const HIGHLIGHT_ANIMATION_TIME = 500;
const EDGE_HIGHLIGHT_LINE_NAME = 'edgeHighlightLine';

interface EdgeHighlightOptions {
  highlightColor?: RGBColorNumber;
  highlightThickness?: number;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const decimalToRgb = (color: RGBColorNumber): Rgb => {
  const r = (color & 0xff0000) >> 16;
  const g = (color & 0x00ff00) >> 8;
  const b = color & 0x0000ff;

  return { r, g, b };
};

const colorToNumber = (color: Rgb): RGBColorNumber => {
  return (color.r << 16) + (color.g << 8) + color.b;
};

const findColorBetween = (left: Rgb, right: Rgb, phase: number): Rgb => {
  const rgbComponents = ['r', 'g', 'b'] as const;
  const [r, g, b] = rgbComponents.map((rgbComponent) =>
    Math.round(
      left[rgbComponent] + (right[rgbComponent] - left[rgbComponent]) * phase,
    ),
  ) as [number, number, number];
  return { r, g, b };
};

const redrawMovedNodeEdge = (
  displayObject: GraphEdgeDisplayObject | undefined,
  edgeOptions: EdgeHighlightOptions,
  edgeData: EdgeData,
) => {
  const edgeLine = displayObject?.getChildByName(EDGE_HIGHLIGHT_LINE_NAME) as
    | Graphics
    | undefined;
  if (edgeData === undefined || !isRenderableEdgeData(edgeData) || !edgeLine) {
    return;
  }
  drawEdgeHighlightArea(
    edgeLine,
    edgeData,
    edgeOptions,
    1,
    edgeOptions.highlightColor,
    edgeOptions.highlightThickness,
  );
};

const getColor = (
  phase: number,
  defaultColor: RGBColorNumber,
  highlightColor?: RGBColorNumber,
  currentColor?: RGBColorNumber,
): number => {
  if (highlightColor !== undefined) {
    return colorToNumber(
      findColorBetween(
        decimalToRgb(defaultColor),
        decimalToRgb(highlightColor),
        phase,
      ),
    );
  }
  return currentColor ?? defaultColor;
};

const drawEdgeHighlightArea = (
  edgeLine: Graphics,
  edgeData: RenderableEdgeData,
  edgeOptions: EdgeHighlightOptions,
  phase: number,
  highlightColor?: RGBColorNumber,
  highlightThickness?: RGBColorNumber,
): Graphics => {
  const defaultThickness =
    edgeData.style?.line?.thickness ?? GraphConstants.EDGE_DEFAULT_THICKNESS;
  const thickness =
    highlightThickness !== undefined
      ? defaultThickness + highlightThickness * phase
      : defaultThickness;
  const defaultColor = getEdgeStyleLineColor(edgeData.style);
  const color = getColor(
    phase,
    defaultColor,
    highlightColor,
    edgeOptions.highlightColor,
  );

  edgeLine.clear();
  // Show edge if it was temporarily hidden
  edgeLine.alpha = 1;
  edgeLine.lineStyle(thickness, color);
  drawEdgeLine(
    edgeLine,
    edgeData.positions.line,
    thickness,
    edgeData.style?.line?.pattern ?? 'solid',
    color,
  );
  drawEdgeArrow(
    edgeLine,
    edgeData.positions.arrowHeads?.source,
    color,
    thickness,
  );
  drawEdgeArrow(
    edgeLine,
    edgeData.positions.arrowHeads?.target,
    color,
    thickness,
  );
  if (edgeData.positions.circle) {
    drawEdgeCircle(edgeLine, edgeData.positions.circle, color);
  }

  return edgeLine;
};

const addHighlightLine = (edgeDisplayObject?: GraphEdgeDisplayObject): void => {
  if (
    edgeDisplayObject !== undefined &&
    edgeDisplayObject.getChildByName(EDGE_HIGHLIGHT_LINE_NAME) === null
  ) {
    const edgeLine = new Graphics();
    edgeLine.name = EDGE_HIGHLIGHT_LINE_NAME;
    edgeLine.zIndex = ZINDEX_EDGE_HIGHLIGHT_LINE;
    edgeDisplayObject.addChild(edgeLine);
  }
};

const getHighlightedLineGraphics = (
  pluginInterface: GraphRendererPluginInterface,
  edgeId: EdgeId,
  createIfNotExists = false,
): Graphics | undefined => {
  const edgeDisplayObject = pluginInterface.edgeDisplayObjects.get(edgeId);
  if (createIfNotExists) {
    addHighlightLine(edgeDisplayObject);
  }
  return edgeDisplayObject?.getChildByName(EDGE_HIGHLIGHT_LINE_NAME) as
    | Graphics
    | undefined;
};

export const edgeHighlight: GraphRendererPlugin = (pluginInterface) => {
  const runningAnimations: Map<EdgeId, RunningAnimation> = new Map();
  const highlightedEdgeOptions: Map<EdgeId, EdgeHighlightOptions> = new Map();

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
  pluginInterface.bindGraphCommandEventListener('highlightEdge', (event) => {
    if (runningAnimations.get(event.edgeId) !== undefined) {
      runningAnimations.get(event.edgeId)?.cancel();
      runningAnimations.delete(event.edgeId);
    }
    const edgeData = pluginInterface.getGraphData().edges[event.edgeId];
    if (edgeData === undefined || !isRenderableEdgeData(edgeData)) {
      return;
    }

    const edgeLine = getHighlightedLineGraphics(
      pluginInterface,
      event.edgeId,
      true,
    );
    const edgeOptions = highlightedEdgeOptions.get(event.edgeId) ?? {};
    if (!edgeLine) {
      return;
    }

    // Prevent the same animation again
    if (
      edgeOptions.highlightColor === event.color &&
      edgeOptions.highlightThickness === event.thickness
    ) {
      return;
    }

    // Save color and thickness to retrieve it when edge is being unhighlighted
    if (event.color !== undefined) {
      edgeOptions.highlightColor = event.color;
    }
    if (event.thickness !== undefined) {
      edgeOptions.highlightThickness = event.thickness;
    }

    drawEdgeHighlightArea(
      edgeLine,
      edgeData,
      edgeOptions,
      0,
      edgeOptions.highlightColor,
      edgeOptions.highlightThickness,
    );

    // Set higher zIndex to show labels above other labels
    const edgeDisplayObject = pluginInterface.edgeDisplayObjects.get(
      event.edgeId,
    );
    if (edgeDisplayObject !== undefined) {
      pluginInterface.graphLayer.setChildIndex(
        edgeDisplayObject,
        pluginInterface.graphLayer.children.length - 1,
      );
    }

    pluginInterface.requestRender();

    highlightedEdgeOptions.set(event.edgeId, edgeOptions);

    const applyAnimationPhase = (phase: number) => {
      const highlightedEdgeLine = getHighlightedLineGraphics(
        pluginInterface,
        event.edgeId,
      );
      const currentEdgeOptions = highlightedEdgeOptions.get(event.edgeId);
      if (highlightedEdgeLine && currentEdgeOptions) {
        drawEdgeHighlightArea(
          highlightedEdgeLine,
          edgeData,
          currentEdgeOptions,
          phase,
          event.color,
          event.thickness,
        );
      }
    };

    const animation = pluginInterface.animate(
      HIGHLIGHT_ANIMATION_TIME,
      applyAnimationPhase,
    );
    runningAnimations.set(event.edgeId, animation);
  });

  const getUnhighlightAttributes = (
    event: UnhighlightEdgeEvent,
  ): Omit<UnhighlightEdgeEvent, 'edgeId'> => {
    // Edge settings was not specified, set edge to default state
    if (event.color === undefined && event.thickness === undefined) {
      return {
        color: true,
        thickness: true,
      };
    }
    // Get user defined settings
    return omit(['edgeId'], event);
  };

  pluginInterface.bindGraphCommandEventListener('unhighlightEdge', (event) => {
    if (runningAnimations.get(event.edgeId) !== undefined) {
      runningAnimations.get(event.edgeId)?.cancel();
      runningAnimations.delete(event.edgeId);
    }
    const edgeData = pluginInterface.getGraphData().edges[event.edgeId];
    if (edgeData === undefined || !isRenderableEdgeData(edgeData)) {
      return;
    }

    const edgeLine = getHighlightedLineGraphics(pluginInterface, event.edgeId);
    const edgeOptions = highlightedEdgeOptions.get(event.edgeId) ?? {};
    if (!edgeLine || !edgeOptions) {
      return;
    }
    const currentHighlightThickness = edgeOptions?.highlightThickness;
    const currentHighlightColor = edgeOptions?.highlightColor;
    const unhighlightAttributes = getUnhighlightAttributes(event);
    if (unhighlightAttributes.color === true) {
      edgeOptions.highlightColor = undefined;
    }
    if (unhighlightAttributes.thickness === true) {
      edgeOptions.highlightThickness = undefined;
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
    const applyAnimationPhase = (phase: number) => {
      const currentEdgeLine = getHighlightedLineGraphics(
        pluginInterface,
        event.edgeId,
      );
      if (currentEdgeLine) {
        drawEdgeHighlightArea(
          currentEdgeLine,
          edgeData,
          edgeOptions,
          1 - phase,
          unhighlightAttributes.color === true
            ? currentHighlightColor
            : undefined,
          unhighlightAttributes.thickness === true
            ? currentHighlightThickness
            : undefined,
        );
      }

      if (phase === 1) {
        const currentEdgeOptions = highlightedEdgeOptions.get(event.edgeId);
        if (
          currentEdgeOptions?.highlightColor === undefined &&
          currentEdgeOptions?.highlightThickness === undefined
        ) {
          highlightedEdgeOptions.delete(event.edgeId);
          const edgeDisplayObject = pluginInterface.edgeDisplayObjects.get(
            event.edgeId,
          );
          if (edgeDisplayObject && currentEdgeLine) {
            edgeDisplayObject.removeChild(currentEdgeLine);
          }
        }

        runningAnimations.delete(event.edgeId);
      }
    };

    const animation = pluginInterface.animate(
      HIGHLIGHT_ANIMATION_TIME,
      applyAnimationPhase,
    );
    runningAnimations.set(event.edgeId, animation);
  });

  pluginInterface.events.addListener(
    'edgeBoundsChange',
    (event: EdgeBoundsChangeEvent) => {
      const edgeOptions = highlightedEdgeOptions.get(event.edgeData.id);
      /**
       * Edge has no saved highlight options.
       * So it's not highlighted and no redraw is necessary.
       */
      if (!edgeOptions) {
        return;
      }
      if (runningAnimations.has(event.edgeData.id)) {
        runningAnimations.get(event.edgeData.id)?.cancel();
        runningAnimations.delete(event.edgeData.id);
      }
      redrawMovedNodeEdge(event.edge, edgeOptions, event.edgeData);

      pluginInterface.requestRender();
    },
  );

  pluginInterface.events.addListener('beforeUnmount', () => {
    highlightedEdgeOptions.clear();
  });
};
